import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useCodeQuizGame from '../../../../hooks/useCodeQuizGame';
import { Button } from '../../../ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../../../ui/card';
import { Progress } from '../../../ui/progress';
import { Badge } from '../../../ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../../../ui/alert';
import useLoginContext from '../../../../hooks/useLoginContext';
import useUserContext from '../../../../hooks/useUserContext';
import ResultsDisplay from './ResultsDisplay';
import QuestionDisplay from './QuestionDisplay';
import BreadcrumbNav, { BreadcrumbItemProps } from '../../../common/BreadcrumbNav';
import { GameStatus } from '../../../../types/types';

interface CodeQuizGamePageProps {
  gameId?: string;
}

/**
 * Component for the Code Quiz Game page.
 * This component manages the display of quiz questions, timer, and game results.
 */
const CodeQuizGamePage: React.FC<CodeQuizGamePageProps> = ({ gameId: propGameId }) => {
  const { gameId: urlGameId } = useParams<{ gameId: string }>();
  const gameId = propGameId || urlGameId || '';
  const { user } = useLoginContext();
  const navigate = useNavigate();

  // Use try-catch to handle potential errors with useUserContext
  try {
    useUserContext();
  } catch (err) {
    // If there's an error with useUserContext, we can just continue
    // The error will be caught in useCodeQuizGame
  }

  const { game, error, joinGame, leaveGame, submitAnswer, timeLeft, timePercentage } =
    useCodeQuizGame(gameId, user?.username || '');

  // Define breadcrumb items
  const breadcrumbItems: BreadcrumbItemProps[] = [
    { label: 'Home', href: '/home' },
    { label: 'Games', href: '/games' },
    { label: 'Code Quiz' },
  ];

  useEffect(() => {
    if (gameId && user?.username) {
      joinGame();
    }

    return () => {
      if (gameId && user?.username) {
        leaveGame();
      }
    };
  }, [gameId, user?.username, joinGame, leaveGame]);

  // Helper function to determine the badge variant based on game status
  const getStatusBadgeVariant = (
    status: GameStatus,
  ): 'default' | 'secondary' | 'outline' | 'destructive' => {
    if (status === 'IN_PROGRESS') return 'default';
    if (status === 'OVER') return 'secondary';
    return 'outline';
  };

  if (!game) {
    return (
      <div className='container mx-auto p-6'>
        <BreadcrumbNav items={breadcrumbItems} />
        <Card>
          <CardHeader>
            <CardTitle>Loading Game...</CardTitle>
          </CardHeader>
          <CardContent>
            {error ? (
              <Alert variant='destructive'>
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : (
              <p>Please wait while we load the game...</p>
            )}
          </CardContent>
          <CardFooter>
            <Button variant='outline' onClick={() => navigate('/games')}>
              Back to Games
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  // Filter out duplicate players
  const uniquePlayers = [...new Set(game.players)];

  return (
    <div className='container mx-auto p-6'>
      <BreadcrumbNav items={breadcrumbItems} />

      <Card className='mb-6'>
        <CardHeader>
          <div className='flex justify-between items-center'>
            <div>
              <CardTitle>Code Quiz Game</CardTitle>
              <CardDescription>Game ID: {game.gameID}</CardDescription>
            </div>
            <Badge variant={getStatusBadgeVariant(game.state.status)}>{game.state.status}</Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className='mb-4'>
            <h3 className='text-sm font-medium mb-1'>Players</h3>
            <div className='flex gap-2 flex-wrap max-h-20 overflow-y-auto p-2'>
              {uniquePlayers.map((player: string) => (
                <Badge key={player} variant={player === user?.username ? 'default' : 'outline'}>
                  {player}
                </Badge>
              ))}
            </div>
          </div>

          {game.state.status === 'WAITING_TO_START' && (
            <Alert>
              <AlertTitle>Waiting for Players</AlertTitle>
              <AlertDescription>
                The game will start once at least 2 players have joined.
              </AlertDescription>
            </Alert>
          )}

          {game.state.status === 'IN_PROGRESS' && (
            <>
              <div className='mb-4'>
                <div className='flex justify-between text-sm mb-1'>
                  <span>Time Remaining</span>
                  <span>{Math.ceil(timeLeft / 1000)} seconds</span>
                </div>
                <Progress value={timePercentage} className='h-2' />
              </div>

              <QuestionDisplay
                question={game.state.questions[game.state.currentQuestionIndex]}
                questionNumber={game.state.currentQuestionIndex + 1}
                totalQuestions={game.state.questions.length}
                onSubmitAnswer={submitAnswer}
                playerID={user?.username || ''}
                playerAnswers={game.state.playerAnswers}
                disabled={false}
              />
            </>
          )}

          {game.state.status === 'OVER' && (
            <ResultsDisplay
              winners={[...(game.state.winners || [])]}
              playerScores={game.state.playerScores}
              questions={game.state.questions}
              playerAnswers={game.state.playerAnswers}
              currentUsername={user?.username || ''}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CodeQuizGamePage;
